import { useSearch } from '@tanstack/react-router'
import { message } from 'antd'
import {
  AssetVizController,
  AssetVizSceneConfig,
  GetSectionable,
} from 'features/assetViz'
import { useGeckoLocale } from 'features/locale'
import {
  type RolodexCacheInstance,
  useRolodexCache,
  useRolodexEntries,
} from 'features/rolodex'
import compact from 'lodash/compact'
import { CustomSectionPages } from 'pages/reporting/reports/rug/components/CustomSectionPages'
import { useLockManagement } from 'pages/reporting/shared/hooks/useLockManagement'
import { useMetadataEntry } from 'pages/reporting/shared/hooks/useMetadataEntry'
import { usePdfManagement } from 'pages/reporting/shared/hooks/UsePDFManagement'
import {
  BaseContextValue,
  PdfProps,
  StaticSection,
} from 'pages/reporting/shared/types'
import { createContext, useCallback, useContext, useMemo } from 'react'
import {
  GeckoAssetModel,
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoComponentDefaults,
  GeckoComponentModel,
  GeckoFormattingVariant,
  GeckoInspection,
  GeckoInspectionFormType,
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoOrganization,
  GeckoSite,
  GeckoUnit,
  GeckoView,
  isInspectionThicknessCDF,
  RolodexEntryType,
  Sectionable,
  SignatureData,
  SignoffState,
} from 'types'
import { groupBy } from 'utils/fde/maps'

import {
  DEFAULT_SENSOR_DIAMETER,
  SENSOR_PAYLOAD_TYPE_TO_FREQUENCY,
} from './constants'
import { AssetPicture, useAssetPictures } from './hooks/useAssetPictures'
import {
  AssetScreenshots,
  useAssetScreenshots,
} from './hooks/useAssetScreenshots'
import { useAssetVizState } from './hooks/useAssetVizState'
import { useCantileverReportData } from './hooks/useCantileverReportData'
import { useFormData } from './hooks/useFormData'
import { useGetStatsBySection } from './hooks/useGetStatsBySection'
import { useReportNavigation } from './hooks/useReportNavigation'
import {
  RugSectionSelection,
  useReportRevisions,
} from './hooks/useReportRevisions'
import { useReportSubmission } from './hooks/useReportSubmission'
import { rugReportForSPA } from './rugReport'
import {
  CoverSectionData,
  DataSheetSectionData,
  ImagesSectionData,
  MainInfo,
  RugFormDataState,
  SectionAddendum,
  SummarySectionData,
} from './rugReportData'
import {
  ComponentId,
  InspectionMetadata,
  RugComponentStats,
  RugSectionStats,
  SensorPayloadMetadata,
  SensorPayloadType,
} from './types'
import { getComponentNominalRange } from './utils/getComponentNominalRange'

export interface RugReportContextValue extends BaseContextValue<RugSectionSelection> {
  // Form Data
  formData: RugFormDataState
  updateFormData: (newData: Partial<RugFormDataState>) => void
  handleSignoffFinish: (values: SignatureData) => Promise<SignoffState>
  revertFormToCantilever: () => Promise<void>
  handleMainInfoFinish: (values: MainInfo) => Promise<void>
  handleCoverSectionFinish: (values: CoverSectionData) => Promise<void>
  handleSummarySectionFinish: (values: SummarySectionData) => Promise<void>
  handleDataSheetSectionFinish: (values: DataSheetSectionData) => Promise<void>
  handleImagesSectionFinish: (values: ImagesSectionData) => Promise<void>
  handleAssetDrawingsFinish: (
    imagePath: string,
    imageType: 'heatmap' | 'no-heatmap' | string,
    index?: number,
    imageCaption?: string
  ) => Promise<void>
  handleSectionAddendumsFinish: (
    sectionAddendums: SectionAddendum[]
  ) => Promise<void>
  forceRefreshPdf: () => Promise<void>

  selectedRevisionData: RugFormDataState | undefined

  assetScreenshots: AssetScreenshots | undefined
  forceRefreshAssetScreenshots: () => Promise<void>
  assetPictures: AssetPicture[]
  forceRefreshAssetPictures: () => Promise<void>
  assetViz: {
    assetVizRef: React.RefObject<AssetVizController>
    assetVizSceneConfig: AssetVizSceneConfig
    aspectRatioRef: React.RefObject<number>
  }
  hasCantileverData: boolean

  // Inspection Data (New for SPA)
  inspections: readonly GeckoInspection[]
  selectedComponents: readonly GeckoComponent[] | undefined
  selectedAssetModel: GeckoAssetModel | undefined
  selectedComponentModels: readonly GeckoComponentModel[] | undefined
  relevantCDFs: GeckoComponentDataFrame[] | undefined
  componentDefaults: readonly GeckoComponentDefaults[]
  organization: GeckoOrganization | undefined
  site: GeckoSite | undefined
  unit: GeckoUnit | undefined

  // Inspection Statistics
  sectionsByComponent: Map<ComponentId, Sectionable | undefined> // This could be courses or wall sections. Some components, such as floors, do not have sections.
  statsBySection: Map<ComponentId, Map<string, RugSectionStats>> | undefined
  componentToNominalRange: Map<ComponentId, GeckoMeasurementRange>
  inspectionMetadata: InspectionMetadata
  statsByComponent: Map<ComponentId, RugComponentStats> | undefined

  // Loading
  isLoading: boolean

  // Formatting wrappers
  formatMeasurement: ({
    measurement,
    variant,
    showLoading,
    minimumFractionDigits,
    maximumFractionDigits,
  }: {
    measurement: GeckoMeasurement | undefined
    variant?: GeckoFormattingVariant | 'none'
    showLoading?: boolean
    minimumFractionDigits?: number
    maximumFractionDigits?: number
  }) => string | undefined
  formatMeasurementRange: ({
    measurement,
    variant,
    showLoading,
    minimumFractionDigits,
    maximumFractionDigits,
  }: {
    measurement: GeckoMeasurementRange | undefined
    variant?: GeckoFormattingVariant | 'none'
    showLoading?: boolean
    minimumFractionDigits?: number
    maximumFractionDigits?: number
  }) => string | undefined
  cdfCache: RolodexCacheInstance
}

interface RugReportProviderProps {
  children: React.ReactNode
  reportID: string
}

export const RugReportContext = createContext<
  RugReportContextValue | undefined
>(undefined)

export const RugReportProvider: React.FC<RugReportProviderProps> = ({
  children,
  reportID,
}: RugReportProviderProps) => {
  const { metadataEntryId: requestedMetadataEntryId } = useSearch({
    strict: false,
  }) as {
    metadataEntryId?: string
  }

  const cdfCache = useRolodexCache()
  const {
    metadataEntryId,
    metadataEntries,
    isLoading: isLoadingMetadata,
    publishRevision,
    revisions,
    currentRevisionNumber,
    createNewRevision,
    latestRevision,
    isCurrentRevisionPublished,
  } = useMetadataEntry(reportID, requestedMetadataEntryId)

  const latestEntry = useMemo(() => {
    return metadataEntries.find((entry) => entry.id === latestRevision?.id)
  }, [metadataEntries, latestRevision])

  const inspectionIds = useMemo(() => {
    return latestEntry?.data.inspections || []
  }, [latestEntry])

  const { organizations, sites, units } = useRolodexEntries(
    latestEntry
      ? {
          type: [RolodexEntryType.REPORTING_METADATA],
          ids: [latestEntry.id],
          links: [
            RolodexEntryType.ORGANIZATION,
            RolodexEntryType.SITE,
            RolodexEntryType.UNIT,
          ],
        }
      : undefined
  )

  const { organization, site, unit } = useMemo(() => {
    return {
      organization: organizations.at(0),
      site: sites.at(0),
      unit: units.at(0),
    }
  }, [organizations, sites, units])

  const {
    inspections: inspections,
    assetModels,
    components: selectedComponents,
    isLoading: isLoadingInspections,
  } = useRolodexEntries(
    latestEntry && inspectionIds.length > 0
      ? [
          {
            type: RolodexEntryType.INSPECTION,
            ids: inspectionIds,
            links: [RolodexEntryType.COMPONENT],
          },
          {
            type: RolodexEntryType.ASSET_MODEL,
            tags: {
              [RolodexEntryType.UNIT]: latestEntry.tags.unit,
            },
          },
        ]
      : undefined
  )

  const {
    componentDataFrames,
    componentDatasets,
    isLoading: isLoadingComponentDataFrames,
  } = useRolodexEntries(
    inspections
      ? [
          {
            type: [
              RolodexEntryType.COMPONENT_DATA_FRAME,
              RolodexEntryType.COMPONENT_DATASET,
            ],
            tags: {
              [RolodexEntryType.INSPECTION]: inspections.map(
                (inspection) => inspection.id
              ),
            },
          },
        ]
      : undefined
  )

  const { componentDefaults } = useRolodexEntries(
    selectedComponents.length > 0
      ? [
          {
            type: RolodexEntryType.COMPONENT_DEFAULTS,
            tags: {
              [RolodexEntryType.COMPONENT]: selectedComponents.map(
                (component) => component.id
              ),
            },
          },
        ]
      : undefined
  )

  const {
    entries: inspectionMetricsEntries,
    isLoading: isLoadingInspectionMetrics,
  } = useRolodexEntries(
    inspections
      ? [
          {
            type: RolodexEntryType.COMBINED_INSPECTION_METRICS,
            tags: {
              [RolodexEntryType.INSPECTION]: inspections.map(
                (inspection) => inspection.id
              ),
            },
          },
        ]
      : undefined
  )

  const { inspectionForms, isLoading: isLoadingInspectionForms } =
    useRolodexEntries(
      inspections
        ? [
            {
              type: RolodexEntryType.INSPECTION_FORM,
              filters: [
                {
                  key: 'form_type',
                  op: '=',
                  value: GeckoInspectionFormType.RUG_INSPECTION_FORM,
                },
              ],
              tags: {
                [RolodexEntryType.INSPECTION]: inspections.map(
                  (inspection) => inspection.id
                ),
              },
            },
          ]
        : undefined
    )

  const rugInspectionForm = useMemo(() => {
    return inspectionForms?.at(0)
  }, [inspectionForms])

  const {
    selectedAssetModel,
    selectedComponentModels,
    relevantThicknessCDFs,
    relevantCDFs,
  } = useMemo(() => {
    const selectedAssetModel = assetModels?.at(0)
    const selectedComponentModels: GeckoComponentModel[] = selectedAssetModel
      ? compact(
          selectedComponents.map((component) =>
            selectedAssetModel.data.components.get(component.id)
          )
        )
      : []
    const cdfsByComponent = groupBy({
      arr: componentDataFrames,
      key: 'tags.component',
    })
    const relevantThicknessCDFs: GeckoComponentDataFrame[] = []
    const relevantCDFs: GeckoComponentDataFrame[] = []
    cdfsByComponent.forEach((cdfs) => {
      const currRelevantThicknessCDFs = cdfs
        .filter(isInspectionThicknessCDF)
        .sort(GeckoComponentDataFrame.GetDefaultSort(componentDatasets))
      relevantThicknessCDFs.push(...currRelevantThicknessCDFs)

      const curRelevantCDFs = cdfs
        .filter(
          (cdf) =>
            cdf.data.params.view !== GeckoView.BinStandardDeviation &&
            cdf.data.params.view !== GeckoView.AScansPerBin
        )
        .sort(GeckoComponentDataFrame.GetDefaultSort(componentDatasets))
      relevantCDFs.push(...curRelevantCDFs)
    })

    return {
      selectedAssetModel,
      selectedComponentModels,
      relevantThicknessCDFs,
      relevantCDFs,
    }
  }, [assetModels, selectedComponents, componentDataFrames, componentDatasets])

  const { hasCantileverData, prePopulatedData } = useCantileverReportData(
    inspections,
    rugInspectionForm
  )

  const {
    formData,
    updateFormData,
    handleSignoffFinish,
    handleMainInfoFinish,
    handleCoverSectionFinish,
    handleSummarySectionFinish,
    handleDataSheetSectionFinish,
    handleImagesSectionFinish,
    handleAssetDrawingsFinish,
    handleSectionAddendumsFinish,
  } = useFormData(
    metadataEntryId,
    metadataEntries,
    isLoadingMetadata,
    prePopulatedData
  )

  const { assetScreenshots, forceRefresh: forceRefreshAssetScreenshots } =
    useAssetScreenshots(metadataEntryId)

  const sectionsByComponent = useMemo(() => {
    const sectionsByComponentMap = new Map<ComponentId, Sectionable>()
    selectedComponentModels.forEach((componentModel) => {
      const sectionable = GetSectionable(componentModel)
      if (sectionable) {
        sectionsByComponentMap.set(
          componentModel.id as ComponentId,
          sectionable
        )
      }
    })
    return sectionsByComponentMap
  }, [selectedComponentModels])

  const report = useMemo(() => {
    if (!formData.sectionAddendums || formData.sectionAddendums.length === 0) {
      return rugReportForSPA
    }

    const existingSectionIds = rugReportForSPA.sections.map(
      (section) => section.id
    )

    const customSectionAddendums = formData.sectionAddendums.filter(
      (addendum) => !existingSectionIds.includes(addendum.sectionId)
    )

    const customSectionsById = customSectionAddendums.reduce(
      (acc, addendum) => {
        if (!acc[addendum.sectionId]) {
          acc[addendum.sectionId] = []
        }
        acc[addendum.sectionId].push(addendum)
        return acc
      },
      {} as Record<string, typeof customSectionAddendums>
    )

    const customStaticSections: StaticSection[] = Object.entries(
      customSectionsById
    ).map(([sectionId, addendums]) => ({
      id: sectionId,
      title: sectionId,
      RenderPDF: ({ onRender }: PdfProps) => (
        <CustomSectionPages
          sectionId={sectionId}
          sectionTitle={sectionId}
          addendums={addendums}
          onRender={onRender}
        />
      ),
    }))

    return {
      ...rugReportForSPA,
      staticSections: [
        ...customStaticSections,
        ...rugReportForSPA.staticSections,
      ],
    }
  }, [formData.sectionAddendums])

  const { activePage, navigateToPage, returnToMenu, completionStatus } =
    useReportNavigation({
      formData,
      report,
      assetScreenshots,
      selectedComponents,
      sectionsByComponent,
    })

  const { assetPictures, forceRefresh: forceRefreshAssetPictures } =
    useAssetPictures(formData)
  const { drawingVizRef, heatmapSceneConfig, aspectRatioRef } =
    useAssetVizState()

  // Revisions
  const {
    selectedRevisionData,
    loadRevisionData,
    copyRevisionContent,
    hasPublishedRevisions,
    canRevertToCantilever,
  } = useReportRevisions({
    metadataEntries,
    currentRevisionNumber,
    revisions,
    onUpdateFormData: updateFormData,
  })

  const {
    latestDraftTimestamp,
    publishLatestDraftPDF,
    handleDownloadLatestDraft,
    handleDownloadPublishedPdf,
    forceRefresh: forceRefreshPdf,
  } = usePdfManagement({
    metadataEntryId,
    formData,
    report,
  })

  // Lock management
  const {
    isLocked,
    startedSignoff,
    lastLockTimestamp,
    handleStartSignoff,
    handleUnlock,
    canStartSignoff,
    canSignAsReviewer,
  } = useLockManagement({
    metadataEntryId,
    metadataWithSignoff: {
      lockState: formData.lockState,
      signoff: formData.signoff,
    },
    updateFormData,
  })

  const { lockState, pdfState } = useMemo(() => {
    return {
      lockState: { isLocked, startedSignoff, lastLockTimestamp },
      pdfState: {
        hasPublishedPdf: !!formData.publishedPdfPath,
        latestDraftTimestamp,
        publishedPdfPath: formData.publishedPdfPath,
      },
    }
  }, [
    formData.publishedPdfPath,
    latestDraftTimestamp,
    isLocked,
    startedSignoff,
    lastLockTimestamp,
  ])

  const {
    isSubmitting,
    handleSubmitReport,
    handleSignoff,
    handlePublishRevision,
  } = useReportSubmission({
    metadataEntryId,
    formData,
    updateFormData,
    inspections,
    currentRevisionNumber,
    publishLatestDraftPDF,
    publishRevision,
  })

  const componentToNominalRange = useMemo(() => {
    const componentToNominalRangeMap = new Map<
      ComponentId,
      GeckoMeasurementRange
    >()
    selectedComponentModels.forEach((componentModel) => {
      const nominalRange = getComponentNominalRange(componentModel)
      if (nominalRange) {
        componentToNominalRangeMap.set(
          componentModel.id as ComponentId,
          nominalRange
        )
      }
    })
    return componentToNominalRangeMap
  }, [selectedComponentModels])

  const inspectionMetadata: InspectionMetadata = useMemo(() => {
    const defaultSensorPayloadFields: SensorPayloadMetadata = {
      payloadType: SensorPayloadType.UNKNOWN,
      sensorFrequency: undefined,
      sensorDiameter: GeckoMeasurement.CreateFromValue(
        DEFAULT_SENSOR_DIAMETER.value,
        DEFAULT_SENSOR_DIAMETER.unit
      ),
      angle: 0,
    }

    if (
      !inspectionMetricsEntries ||
      inspectionMetricsEntries.length === 0 ||
      isLoadingInspectionMetrics
    ) {
      return {
        inspectionSoftwareVersion: undefined,
        reported_ascan_count: undefined,
        sensorPayloadMetadata: defaultSensorPayloadFields,
      }
    }

    // Extract metadata from all inspections
    const allMetadata = compact(
      inspectionMetricsEntries.map((entry) => {
        const data = entry?.data
        if (!data) return null

        const sensorPayload = data.sensor_payload as
          | SensorPayloadType
          | undefined
        const sensorFrequency = sensorPayload
          ? SENSOR_PAYLOAD_TYPE_TO_FREQUENCY[sensorPayload]
          : undefined

        const rcVersions: string[] =
          (data.rc_versions as string[] | undefined) ?? []
        const inspectionSoftwareVersion =
          rcVersions && rcVersions.length > 0 ? rcVersions[0] : undefined
        const reported_ascan_count = data.reported_ascan_count as
          | number
          | undefined

        return {
          inspectionSoftwareVersion,
          reported_ascan_count,
          sensorPayload,
          sensorFrequency,
        }
      })
    )

    // Check if all inspections have the same metadata values
    if (allMetadata.length > 1) {
      const firstMetadata = allMetadata[0]
      const allMatch = allMetadata.every(
        (metadata) =>
          metadata?.inspectionSoftwareVersion ===
            firstMetadata?.inspectionSoftwareVersion &&
          metadata?.reported_ascan_count ===
            firstMetadata?.reported_ascan_count &&
          metadata?.sensorPayload === firstMetadata?.sensorPayload
      )

      if (!allMatch) {
        message.warning(
          'Inspection metadata values differ across selected inspections. Using default values.'
        )
        return {
          inspectionSoftwareVersion: undefined,
          reported_ascan_count: undefined,
          sensorPayloadMetadata: defaultSensorPayloadFields,
        }
      }
    }

    // If we get here, all metadata matches (or we only have one inspection)
    const metadata = allMetadata.at(0)
    if (!metadata) {
      return {
        inspectionSoftwareVersion: undefined,
        reported_ascan_count: undefined,
        sensorPayloadMetadata: defaultSensorPayloadFields,
      }
    }

    return {
      inspectionSoftwareVersion: metadata.inspectionSoftwareVersion,
      reported_ascan_count: metadata.reported_ascan_count,
      sensorPayloadMetadata: {
        ...defaultSensorPayloadFields,
        payloadType: metadata.sensorPayload ?? SensorPayloadType.UNKNOWN,
        sensorFrequency: metadata.sensorFrequency,
      },
    }
  }, [inspectionMetricsEntries, isLoadingInspectionMetrics])

  const revertFormToCantilever = useCallback(async () => {
    // TODO: Implement revert logic
  }, [])

  const {
    formatMeasurement: formatMeasurementOriginal,
    formatMeasurementRange: formatMeasurementRangeOriginal,
  } = useGeckoLocale()

  const formatMeasurement = useCallback(
    ({
      measurement,
      variant,
      showLoading,
      minimumFractionDigits,
      maximumFractionDigits,
    }: {
      measurement: GeckoMeasurement | undefined
      variant?: GeckoFormattingVariant | 'none'
      showLoading?: boolean
      minimumFractionDigits?: number
      maximumFractionDigits?: number
    }): string | undefined => {
      const result = formatMeasurementOriginal({
        measurement,
        variant,
        showLoading,
        minimumFractionDigits,
        maximumFractionDigits,
      })
      return result?.replace(/″/g, '"')?.replace(/′/g, "'")
    },
    [formatMeasurementOriginal]
  )

  const formatMeasurementRange = useCallback(
    ({
      measurement,
      variant,
      showLoading,
      minimumFractionDigits,
      maximumFractionDigits,
    }: {
      measurement: GeckoMeasurementRange | undefined
      variant?: GeckoFormattingVariant | 'none'
      showLoading?: boolean
      minimumFractionDigits?: number
      maximumFractionDigits?: number
    }): string | undefined => {
      const result = formatMeasurementRangeOriginal({
        measurement,
        variant,
        showLoading,
        minimumFractionDigits,
        maximumFractionDigits,
      })
      return result?.replace(/″/g, '"')?.replace(/′/g, "'")
    },
    [formatMeasurementRangeOriginal]
  )

  const {
    statsBySection,
    statsByComponent,
    isLoading: isLoadingStatsBySection,
  } = useGetStatsBySection({
    sectionsByComponent,
    selectedComponentModels,
    relevantThicknessCDFs,
    assetModel: selectedAssetModel,
    componentDataFrames,
    isLoadingData: isLoadingComponentDataFrames || isLoadingInspections,
  })

  const contextValue = useMemo(() => {
    return {
      // Base context values
      activePage,
      report,
      navigateToPage,
      returnToMenu,
      completionStatus,

      isLoading:
        isLoadingMetadata ||
        isLoadingInspections ||
        isLoadingComponentDataFrames ||
        isLoadingStatsBySection ||
        isLoadingInspectionMetrics ||
        isLoadingInspectionForms,
      formData,
      updateFormData,
      handleSignoffFinish,
      revertFormToCantilever,
      handleMainInfoFinish,
      handleCoverSectionFinish,
      handleSummarySectionFinish,
      handleDataSheetSectionFinish,
      handleImagesSectionFinish,
      handleAssetDrawingsFinish,
      handleSectionAddendumsFinish,
      inspections,
      selectedComponents,
      selectedAssetModel,
      selectedComponentModels,
      relevantCDFs,
      componentDefaults,
      sectionsByComponent,
      componentToNominalRange,
      inspectionMetadata,
      statsBySection,
      statsByComponent,
      organization,
      site,
      unit,

      // Revisions
      metadataEntryId,
      currentRevisionNumber,
      revisions,
      selectedRevisionData,
      loadRevisionData,
      copyRevisionContent,
      hasPublishedRevisions,
      canRevertToCantilever,
      createNewRevision,
      isCurrentRevisionPublished,

      // Submissions
      isSubmitting,
      handleSubmitReport,
      handleSignoff,
      handlePublishRevision,

      // Lock Management
      lockState,
      handleStartSignoff,
      handleUnlock,
      canStartSignoff,
      canSignAsReviewer,

      // PDF Management
      pdfState,
      publishLatestDraftPDF,
      handleDownloadLatestDraft,
      handleDownloadPublishedPdf,
      forceRefreshPdf,
      assetScreenshots,
      assetPictures,
      forceRefreshAssetScreenshots,
      forceRefreshAssetPictures,
      assetViz: {
        assetVizRef: drawingVizRef,
        assetVizSceneConfig: heatmapSceneConfig,
        aspectRatioRef,
      },
      hasCantileverData,
      formatMeasurement,
      formatMeasurementRange,
      cdfCache,
    }
  }, [
    activePage,
    report,
    navigateToPage,
    returnToMenu,
    completionStatus,
    isLoadingMetadata,
    isLoadingInspections,
    isLoadingComponentDataFrames,
    isLoadingStatsBySection,
    isLoadingInspectionMetrics,
    isLoadingInspectionForms,
    formData,
    updateFormData,
    handleSignoffFinish,
    revertFormToCantilever,
    handleMainInfoFinish,
    handleCoverSectionFinish,
    handleSummarySectionFinish,
    handleDataSheetSectionFinish,
    handleImagesSectionFinish,
    handleAssetDrawingsFinish,
    handleSectionAddendumsFinish,
    inspections,
    selectedComponents,
    selectedAssetModel,
    selectedComponentModels,
    relevantCDFs,
    componentDefaults,
    sectionsByComponent,
    componentToNominalRange,
    inspectionMetadata,
    statsBySection,
    statsByComponent,
    organization,
    site,
    unit,
    metadataEntryId,
    currentRevisionNumber,
    revisions,
    selectedRevisionData,
    loadRevisionData,
    copyRevisionContent,
    hasPublishedRevisions,
    canRevertToCantilever,
    createNewRevision,
    isCurrentRevisionPublished,
    isSubmitting,
    handleSubmitReport,
    handleSignoff,
    handlePublishRevision,
    lockState,
    handleStartSignoff,
    handleUnlock,
    canStartSignoff,
    canSignAsReviewer,
    pdfState,
    publishLatestDraftPDF,
    handleDownloadLatestDraft,
    handleDownloadPublishedPdf,
    forceRefreshPdf,
    assetScreenshots,
    assetPictures,
    forceRefreshAssetScreenshots,
    forceRefreshAssetPictures,
    drawingVizRef,
    heatmapSceneConfig,
    aspectRatioRef,
    hasCantileverData,
    formatMeasurement,
    formatMeasurementRange,
    cdfCache,
  ])

  return (
    <RugReportContext.Provider value={contextValue}>
      {children}
    </RugReportContext.Provider>
  )
}

export const useRugReportOps = () => {
  const context = useContext(RugReportContext)
  if (!context) {
    throw new Error('useRugReportOps must be used within a RugReportProvider')
  }
  return context
}
